import DividerWithText from "../../../../components/DividerWithText";
import DivContainerBody from "../../../../components/DivContainerBody";

import {
    Box,
    InputAdornment,
    MenuItem
} from "@mui/material";
import React, { useEffect } from "react";
import {CustomTextField} from "../../../../components/CustomTextField";
import {useSelector} from "react-redux";
import {CustomSelectAndChildren} from "../../../../components/CustomSelectAndChildren";
import {
    ANTIPASSBACKTYPE_MODEL,
    DEVICE_MODEL_NAME,
    DISABLE_REQUEST_SIGNATURE_ALWAYS_BY_MODE,
    STEPS_CREATE_DEVICE
} from "../Model/Model";
import ButtonIcon from "../../../../components/buttons/ButtonIcon";
import {CleanIcon} from "../../../../components/buttons/IconButtons";
import CustomTimePicker from "../../../../components/CustomTimePicker";
import {timeStringToDate} from "../../../../utils/TimeConversion";
import { DIGITS_REGEX } from "../../../../Apis/functions";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * SectionCreateDeviceSecurity Component - Security configuration section for device management.
 * Handles advanced security settings including anti-passback systems, escort modes, biometric checks,
 * and device synchronization. Features complex conditional logic based on device type and state.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} data - Device security configuration data
 * @param {Function} handleChange - Universal change handler
 * @param {Function} t - Translation function
 * @param {boolean} formSubmitted - Form submission status
 * @param {boolean} emptyFields - Validation state flag
 * @param id - id by the device
 * @param handleClear - functions to clear the inputs
 * @returns {JSX.Element}
 * @constructor
 */


const SectionCreateDeviceSecurity = ({data, handleChange, t, formSubmitted, emptyFields, id, handleClear, disabled}) => {
    const {getAntiPassBackType, getEscortMode} = useSelector((state) => state.DevicesSlice);
    const {getAllMedia} = useSelector((state) => state.CommonsSlice);

    const disabledAntiPassBack = [
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR]

    const disabledAntiPassBackTime = [
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data?.data?.deviceType) || data?.security.antiPassBackType === ANTIPASSBACKTYPE_MODEL.NONE;

    const disabledAntiPassBackAlert = data?.security?.antiPassBackType === ANTIPASSBACKTYPE_MODEL.NONE
        || [
            DEVICE_MODEL_NAME.ANTENNA_RFID,
            DEVICE_MODEL_NAME.CAMERA_LPR].includes(data?.data?.deviceType);

    const disabledSyncTimeValue = [
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.data?.deviceType);

    // Validation states
    const emptyAntiPassBack = emptyFields && ![
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,
        DEVICE_MODEL_NAME.ARATEK_BD8500].includes(data.data?.deviceType);
    const emptyAntiPassBackTime = formSubmitted
        && emptyFields
        && ![
            DEVICE_MODEL_NAME.ANTENNA_RFID,
            DEVICE_MODEL_NAME.CAMERA_LPR].includes(data.data?.deviceType)
        && (data.antiPassBackType !== 1 && (!data.security?.antiPassBackTime || data?.security.antiPassBackTime <= 0));

    const emptyAntiPassBackAlert = emptyFields && ![
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR].includes(data?.data?.deviceType)
        && data.antiPassBackType !== ANTIPASSBACKTYPE_MODEL.NONE;

    const emptyEscortMode = emptyFields && ![
        DEVICE_MODEL_NAME.ANTENNA_RFID,
        DEVICE_MODEL_NAME.CAMERA_LPR,].includes(data?.data?.deviceType);


    const showRequestSignatureAlwaysMode = React.useMemo(() => {
        const device = data?.data?.deviceType;
        const mode = data?.data?.modeId;

        const isCombinationAllowed = !!DISABLE_REQUEST_SIGNATURE_ALWAYS_BY_MODE[device]?.includes(mode);

        return !isCombinationAllowed;
    }, [data?.data?.deviceType, data?.data?.modeId]);

    return (
        <DivContainerBody>
            <div className={"row"}>
                <div className='d-flex justify-content-end col-12 mb-2'>
                    {id !== undefined ? (
                        ''
                    ) : (
                        <ButtonIcon
                            className='btn-clean-secondary-600'
                            id='btn_cud_clean_all_inputs'
                            Icon={CleanIcon}
                            title={'clear_instances'}
                            onClick={() => {
                                handleClear(STEPS_CREATE_DEVICE.SECURITY.INDEX);
                            }}
                            iconProps='btn-icon-svg-12px icon-color-general-00'
                        />
                    )}
                </div>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_scds_anti_pass_back'
                        name='antiPassBackType'
                        emptyFields={emptyAntiPassBack}
                        value={data?.security?.antiPassBackType}
                        trans='anti_pass_back'
                        required={true}
                        onChange={(e) => { handleChange(e, "antiPassBackType", 'security') }}
                        disabled={disabled || disabledAntiPassBack.includes(data?.data?.deviceType)}
                        showNoSelectionOption={false}
                    >
                        {getAntiPassBackType?.map((item, index) => (
                            <MenuItem key={index} value={item.id}>
                                {item.id === 1 ? t('none') : item.name}
                            </MenuItem>
                        ))}
                </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_scds_anti_pass_back_time'
                        name='antiPassBackTime'
                        emptyFields={emptyAntiPassBackTime}
                        isValid={!(data?.security?.antiPassBackType !== 1 && (!data?.security?.antiPassBackTime || data?.security?.antiPassBackTime <= 0))}
                        isValidText={'this_value_must_be_greater_than_0'}
                        value={data?.security?.antiPassBackTime ?? 0}
                        type = 'number'
                        trans='anti_pass_back_time'
                        required={true}
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleChange(
                                    { target: { value: 0 } },
                                    "antiPassBackTime",
                                    'security'
                                );
                            }
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (DIGITS_REGEX.test(value) && (value === '' || Number(value) >= 0)) {
                                handleChange(e, "antiPassBackTime", 'security');
                            }
                        }}
                        length={4}
                        disabled={disabled || disabledAntiPassBackTime}
                        helperText={'this_value_must_be_greater_than_0'}
                        InputProps={{
                            min: 0,
                            endAdornment: <InputAdornment position='end'>{t('sec')}</InputAdornment>,
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_scds_anti_pass_back_alert'
                        name='antiPassBackAlert'
                        emptyFields={emptyAntiPassBackAlert}
                        value={data?.security?.antiPassBackAlert}
                        trans='anti_pass_back_alert_by'
                        required={true}
                        onChange={(evt) => { handleChange(evt, "antiPassBackAlert", 'security') }}
                        disabled={disabled || disabledAntiPassBackAlert}
                        showNoSelectionOption={false}
                    >
                        {getAllMedia?.map((item) => {
                            return <MenuItem value={item.id}>{item.name}</MenuItem>;
                        })}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_scds_escort'
                        name='escortMode'
                        emptyFields={emptyEscortMode}
                        value={data?.security?.escortMode}
                        trans='escort'
                        onChange={(evt) => { handleChange(evt, "escortMode",'security') }}
                        disabled={true}
                    >
                        {getEscortMode?.map((item) => {
                            return (
                                <MenuItem value={item.id}>
                                    {(item?.id === 2 && t('needed').toUpperCase()) ||
                                    (item?.id === 1 && t('none').toUpperCase())}
                                </MenuItem>
                            );
                        })}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_scds_request_signature'
                        name='requestSignatureAlways'
                        value={data?.security?.requestSignatureAlways}
                        trans='request_signature'
                        onChange={(evt) => { handleChange(evt, "requestSignatureAlways", 'security') }}
                        disabled={disabled || showRequestSignatureAlwaysMode}
                        showNoSelectionOption={false}
                    >
                        <MenuItem value={true}>{t('YES')}</MenuItem>
                        <MenuItem value={false}>{t('NO')}</MenuItem>
                    </CustomSelectAndChildren>
                </Box>
                    <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                        <CustomTextField
                            id='txt_cud_request_signature'
                            name='requestSignature'
                            trans='request_signature_every'
                            type={'number'}
                            value={data?.security?.requestSignature ?? 0}
                            onBlur={(e) => {
                                if (e.target.value === '') {
                                    handleChange(
                                        { target: { value: 0 } },
                                        "requestSignature",
                                        'security'
                                    );
                                }
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (DIGITS_REGEX.test(value) && (value === '' || Number(value) >= 0)) {
                                    handleChange(e, "requestSignature", 'security');
                                }
                            }}
                            length={4}
                            showCounter={false}
                            disabled={disabled || showRequestSignatureAlwaysMode}
                            helperText={t('fill_0_to_make_it_everytime')}
                            InputProps={{
                                min : 0,
                                endAdornment: (
                                    <InputAdornment className='endAdornmentStyle' position='end'>
                                        {t('days')}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_validation_number'
                        name='validationNumber'
                        value={data?.security?.validationNumber ?? 0}
                        type={'number'}
                        trans='validation_number'
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleChange(
                                    { target: { value: 0 } },
                                    "validationNumber",
                                    'security'
                                );
                            }
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (DIGITS_REGEX.test(value) && (value === '' || Number(value) >= 0)) {
                                handleChange(e, "validationNumber", 'security');
                            }
                        }}
                        length={4}
                        showCounter={false}
                        disabled={true}
                        InputProps={{
                            min : 0
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_scds_sync_time'
                        name='syncTime'
                        type={'number'}
                        value={data?.security?.syncTime ?? 0}
                        trans='sync_time'
                        disabled={disabled || disabledSyncTimeValue}
                        emptyFields={!disabledSyncTimeValue}
                        required={true}
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleChange(
                                    { target: { value: 0 } },
                                    "syncTime",
                                    'security'
                                );
                            }
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (DIGITS_REGEX.test(value) && (value === '' || Number(value) >= 0)) {
                                handleChange(e, "syncTime", 'security');
                            }
                        }}
                        length={4}
                        helperText={t('fill_0_to_make_it_everytime')}
                        InputProps={{
                            min : 0,
                            endAdornment: (
                                <InputAdornment className='endAdornmentStyle' position='end'>
                                    HR
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_scds_real_time_sync'
                        name='realTimeSync'
                        value={data?.security?.realTimeSync}
                        trans='real_time_sync'
                        required={true}
                        onChange={(evt) => { handleChange(evt, "realTimeSync", 'security') }}
                        disabled={disabled || disabledSyncTimeValue}
                        showNoSelectionOption={false}
                    >
                        <MenuItem value={true}>{t('YES')}</MenuItem>
                        <MenuItem value={false}>{t('NO')}</MenuItem>
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTimePicker
                        id='tp_scds_sync_at_time'
                        title={t('sync_at_time')}
                        name='syncAtTime'
                        inputFormat='HH:mm'
                        value={timeStringToDate(data?.security?.syncAtTime)}
                        handleChange={(value) => handleChange({ target: { value } }, "syncAtTime", 'security')}
                        disabled={disabled || disabledSyncTimeValue}
                        required={!disabledSyncTimeValue}
                        emptyFields={emptyFields && !disabledSyncTimeValue}
                        ampm={false}
                        formatValue={true}
                        views={['hours', 'minutes']}
                    />
                </Box>

            </div>
        </DivContainerBody>
    );
};

export default SectionCreateDeviceSecurity;
